import { useState } from 'react'
import { useTrustScore } from './useTrustScore'
import { tierDotStyle } from './tierStyles'
import { MAX_TRUST_SCORE } from './types'
import TrustScoreOverridePanel from './TrustScoreOverridePanel'

interface Props {
  vendorUserId: number
  size:         'sm' | 'md'
}

export default function TrustScoreBadge({ vendorUserId, size }: Props) {
  const { data, isLoading, isError, refetch } = useTrustScore(vendorUserId)
  const [panelOpen, setPanelOpen] = useState(false)

  if (isError) return <span aria-hidden />

  if (isLoading || !data) {
    if (size === 'sm') {
      return (
        <span className="ts-mini" style={{ color: 'var(--ink4)', fontSize: 11 }}>
          —/{MAX_TRUST_SCORE}
        </span>
      )
    }
    return null
  }

  const prefix = data.isOverridden ? '~' : ''
  const dotStyle = tierDotStyle(data.tier)

  if (size === 'sm') {
    return (
      <span
        className="ts-mini"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: 'var(--ink3)',
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          borderRadius: 999,
          padding: '2px 8px',
        }}
      >
        {prefix}{data.effectiveScore}/{MAX_TRUST_SCORE}
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            ...dotStyle,
          }}
        />
      </span>
    )
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--ink)' }}>
        {prefix}{data.effectiveScore}/{MAX_TRUST_SCORE}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, alignItems: 'center' }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
            border: '1px solid var(--line)',
            color: 'var(--ink3)',
            background: 'var(--bg)',
          }}
        >
          {data.tierLabel}
        </span>
        {data.isOverridden && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--amber)',
              color: 'var(--ink)',
            }}
          >
            Overridden
          </span>
        )}
      </div>
      <button
        type="button"
        className="btn b-ol btn-sm"
        style={{ marginTop: 10 }}
        onClick={() => setPanelOpen(o => !o)}
      >
        {panelOpen
          ? 'Hide override'
          : data.isOverridden
            ? 'Edit override'
            : 'Override score'}
      </button>
      {panelOpen && (
        <TrustScoreOverridePanel
          vendorUserId={vendorUserId}
          currentScore={data.effectiveScore}
          computedScore={data.computedScore}
          isOverridden={data.isOverridden}
          overrideScore={data.overrideScore}
          onSave={() => { void refetch(); setPanelOpen(false) }}
        />
      )}
    </div>
  )
}
